import React from 'react';

import GridWrapper from '../../../shared/components/layout/GridWrapper';

import { useQuery } from '@tanstack/react-query';

import {
  getManagementDashboardStats,
  getProjectByID
} from '../../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import ProjectBuilding from '../../../shared/assets/png/Project_Building.webp';
import { Building2, CheckCircle2, Landmark, MapPin, Pencil, Trash2 } from 'lucide-react';
import { LateFee } from './../../pages/LateFee';
import Loader from '../../../shared/components/ui/Loader';

// Conversion logic
const parseAreaType = (areaType) => {
  if (!areaType) return '';

  let type = areaType.toLowerCase().trim();
  type = type.replace('sq/', '');
  type = type.replace(/\s+/g, '');

  if (type.includes('guntha')) type = type.replace('guntha', 'gunta');
  if (type === 'gunta' || type === 'guntha') type = 'gunta';

  if (type === 'acres') type = 'acre';

  if (type === 'hectares') type = 'hectare';

  return type;
};

const calculateFinalArea = (mainArea, mainAreaType, subArea, subAreaType) => {
  const mArea = parseFloat(mainArea) || 0;
  const sArea = parseFloat(subArea) || 0;

  const mType = parseAreaType(mainAreaType);
  const sType = parseAreaType(subAreaType);

  let finalMainArea = mArea;
  let finalMainAreaType = mType;
  let finalSubArea = sArea;
  let finalSubAreaType = sType;

  if (sType === 'gunta') {
    if (mType === 'acre') {
      const gunthaPerAcre = 40;
      const acresFromSub = Math.floor(sArea / gunthaPerAcre);
      const leftoverGunta = sArea % gunthaPerAcre;

      finalMainArea = mArea + acresFromSub;
      finalMainAreaType = 'acre';
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    } else if (mType === 'hectare') {
      const gunthaPerHectare = 98.84;
      const totalHectaresFromSub = sArea / gunthaPerHectare;

      const integerHectares = Math.floor(totalHectaresFromSub);
      const fractionalHectares = totalHectaresFromSub - integerHectares;

      finalMainArea = mArea + integerHectares;
      finalMainAreaType = 'hectare';

      const leftoverGunta = fractionalHectares * gunthaPerHectare;
      finalSubArea = leftoverGunta;
      finalSubAreaType = 'gunta';
    }
  }

  // Format the guntha value to two decimal points
  finalSubArea = parseFloat(finalSubArea.toFixed(2));

  return { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType };
};

const ProjectOverview = ({ onEditProject, onDeleteProject, isDeleteSubmitting = false }) => {
  const { id: projectId } = useParams();

  /* 
    Data fetching
  */
  const {
    data: project,
    isLoading: isProjectLoading,
    isFetching: isProjectFetching
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectByID(projectId)
  });
  const {
    data: dashboardStatsResponse,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching
  } = useQuery({
    queryKey: ['managementDashboardStats', projectId],
    queryFn: () => getManagementDashboardStats(projectId),
    enabled: !!projectId
  });

  const isPageLoading =
    isProjectLoading ||
    isProjectFetching ||
    isStatsLoading ||
    isStatsFetching;
  const dashboardStats = dashboardStatsResponse?.data || {};
  const unitsData = dashboardStats?.units || {};

  const { finalMainArea, finalMainAreaType, finalSubArea, finalSubAreaType } = calculateFinalArea(
    project?.land?.total_mainArea,
    project?.land?.total_mainAreaType,
    project?.land?.total_subArea,
    project?.land?.total_subAreaType
  );

  const displayArea =
    finalSubArea === 0
      ? `${finalMainArea} ${finalMainAreaType}`
      : `${finalMainArea} ${finalMainAreaType}, ${finalSubArea} ${finalSubAreaType}`;

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(String(value).replaceAll(',', ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalUnits = toNumber(
    unitsData?.totalUnits ??
      project?.total_units ??
      project?.totalUnits ??
      project?.units_count ??
      project?.no_of_units
  );
  const unitsSold = toNumber(
    unitsData?.booked ??
      project?.units_sold ??
      project?.sold_units ??
      project?.unitsSold ??
      project?.booked_units
  );
  const unsoldUnits = toNumber(unitsData?.vacant ?? Math.max(totalUnits - unitsSold, 0));

  const overdueCollection = toNumber(dashboardStats?.overdueCollection);
  function convertDateToDDMMYYYY(dateString) {
    // Parse the input into a Date object
    const date = new Date(dateString);

    // Check for invalid date
    if (isNaN(date)) {
      return '';
    }

    // Extract day, month, and year
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getUTCFullYear();

    // Return in dd/mm/yyyy format
    return `${day}/${month}/${year}`;
  }

  const getStatusLabel = () => {
    const status = String(project?.status || project?.project_status || 'ongoing').trim();
    return status || 'ongoing';
  };
  const statusLabel = getStatusLabel();

  const location =
    [project?.land?.city, project?.land?.state].filter(Boolean).join(', ') ||
    project?.land?.address ||
    'Location not available';
  const surveyLabel =
    project?.land?.state === 'Karnataka' || project?.land?.state === 'Gujarat'
      ? 'Survey Number'
      : 'Khasra/Survey Number';
  const surveyValue =
    project?.land?.khasra
      ?.map((item) => item.number)
      .filter(Boolean)
      .join(', ') || '--';
  const permissionNumbers =
    project?.buildings
      ?.map((item) => item?.permissionNo || item?.permission_no)
      ?.filter(Boolean)
      ?.join(', ') || '--';
  // Build-level legal fields are optional/inconsistent in API responses,
  // so we normalize once and drive all overview badges/details from this list.
  const buildings = Array.isArray(project?.buildings) ? project.buildings : [];
  // Show RERA verification only when at least one building carries a valid RERA number.
  const hasReraVerified = buildings.some((item) => String(item?.rera_registration_no || '').trim());
  const reraNumbers =
    buildings
      .map((item) => item?.rera_registration_no)
      .filter(Boolean)
      .join(', ') || '--';
  // `rera_permission_tenure` is a year count (e.g. "5"), not an ISO date — do not pass it to `new Date`.
  const getBuildingPermissionEndDate = (item) => {
    const endRaw = item?.permissionEndDate ?? item?.permission_end_date;
    if (endRaw) {
      const d = new Date(endRaw);
      if (!Number.isNaN(d.getTime())) return d;
    }
    const approvalRaw = item?.rera_approval_date;
    const tenureYears = Number(String(item?.rera_permission_tenure ?? '').trim());
    if (!approvalRaw || !Number.isFinite(tenureYears) || tenureYears <= 0) return null;
    const approval = new Date(approvalRaw);
    if (Number.isNaN(approval.getTime())) return null;
    const end = new Date(approval.getTime());
    end.setUTCFullYear(end.getUTCFullYear() + tenureYears);
    return end;
  };
  const permissionEndDates = buildings
    .map(getBuildingPermissionEndDate)
    .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
  const hasActivePermission = permissionEndDates.some((date) => date >= new Date());
  const earliestPermissionEnd =
    permissionEndDates.length > 0
      ? permissionEndDates.reduce((earliest, d) => (d < earliest ? d : earliest))
      : null;
  const reraExpiry =
    earliestPermissionEnd && !Number.isNaN(earliestPermissionEnd.getTime())
      ? convertDateToDDMMYYYY(earliestPermissionEnd.toISOString()) || '--'
      : '--';
  const permissionStatusLabel = hasActivePermission
    ? 'Permission Active'
    : 'Permission Expired/Review Needed';
  const googleMapUrl = project?.land?.google_map_link || project?.google_location_link || '';
  const googlePreviewImageUrl =
    project?.googleImage?.[0]?.url || project?.land?.google_map_image_link || '';

  const formatCompactCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value || 0);

  return (
    <>
      <Loader isVisible={isPageLoading} />
      <div className="w-full bg-[#F3F5F7] pb-4 overflow-x-hidden">
      <GridWrapper width={100}>
        <div className="space-y-3">
          <section className="rounded-xl border border-[#E5EAF1] bg-white p-3 md:p-4 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-start gap-3">
              <div className="flex flex-1 min-w-0 items-start gap-2.5">
                <div className="h-16 w-16 rounded-xl overflow-hidden border border-[#DCE3EA] bg-[#F2F5F8] shrink-0">
                  <img
                    src={project?.projectImages?.[0]?.url || ProjectBuilding}
                    alt="Project"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-[30px] leading-tight font-bold text-[#28454C] truncate">
                      {project?.name || 'Project Name'}
                    </h2>
                    <span className="inline-flex items-center rounded-md bg-[#DCFCE7] px-2 py-1 text-[11px] font-semibold uppercase text-[#166534]">
                      {statusLabel}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[#64748B]">
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#BFE3F3] bg-[#E0F2FE] px-2.5 py-1 text-xs font-semibold text-[#0369A1]">
                      <Building2 size={13} /> {project?.project_type || 'Project Type'}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#BBF7D0] bg-[#DCFCE7] px-2.5 py-1 text-xs font-semibold text-[#166534]">
                      <MapPin size={13} /> {project?.land?.state || location}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 font-medium ${
                        hasReraVerified ? 'text-[#2563EB]' : 'text-[#94A3B8]'
                      }`}>
                      <CheckCircle2 size={14} />{' '}
                      {hasReraVerified ? 'RERA Verified' : 'RERA Not Verified'}
                    </span>
                  </div>
                  <p
                    className={`mt-2 text-sm font-semibold ${
                      hasActivePermission ? 'text-[#0F766E]' : 'text-[#DC2626]'
                    }`}>
                    {permissionStatusLabel}
                  </p>
                </div>
              </div>
              <div className="flex self-start md:ml-3 md:shrink-0 gap-2">
                <button
                  type="button"
                  onClick={onEditProject}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#D6DEE8] bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#334155] hover:bg-[#F8FAFC]">
                  <Pencil size={14} />
                  Edit Project
                </button>
                <button
                  type="button"
                  onClick={onDeleteProject}
                  disabled={!onDeleteProject || isDeleteSubmitting}
                  aria-label="Delete project"
                  title="Delete project"
                  className="inline-flex items-center justify-center rounded-lg border border-[#FECACA] bg-white p-2 text-[#DC2626] hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-50">
                  <Trash2 size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5">
            <div className="min-h-[92px] rounded-2xl border border-[#C7D2FE] bg-white px-3 py-2.5">
              <div className="flex h-full items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EEF2FF] text-[#4F46E5]">
                  <Building2 size={16} />
                </span>
                <div className="min-w-0">
                  <p className="text-[32px] leading-none font-bold text-[#312E81]">{totalUnits}</p>
                  <p className="mt-0.5 text-[10px] tracking-wide font-semibold uppercase text-[#64748B]">
                    Total Units
                  </p>
                </div>
              </div>
            </div>
            <div className="min-h-[92px] rounded-2xl border border-[#A7F3D0] bg-white px-3 py-2.5">
              <div className="flex h-full items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ECFDF5] text-[#0F766E]">
                  <CheckCircle2 size={16} />
                </span>
                <div className="min-w-0">
                  <p className="text-[32px] leading-none font-bold text-[#0F766E]">{unitsSold}</p>
                  <p className="mt-0.5 text-[10px] tracking-wide font-semibold uppercase text-[#9AA6B2]">
                    Units Sold (Booked)
                  </p>
                </div>
              </div>
            </div>
            <div className="min-h-[92px] rounded-2xl border border-[#FCD9A8] bg-white px-3 py-2.5">
              <div className="flex h-full items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FFF7ED] text-[#B45309]">
                  <MapPin size={16} />
                </span>
                <div className="min-w-0">
                  <p className="text-[32px] leading-none font-bold text-[#B45309]">{unsoldUnits}</p>
                  <p className="mt-0.5 text-[10px] tracking-wide font-semibold uppercase text-[#9AA6B2]">
                    Unsold Units (Vacant)
                  </p>
                </div>
              </div>
            </div>
            <div className="min-h-[92px] rounded-2xl border border-[#BAE6FD] bg-white px-3 py-2.5">
              <div className="flex h-full items-center gap-2.5">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E0F2FE] text-[#0369A1]">
                  <Landmark size={16} />
                </span>
                <div className="min-w-0">
                  <p className="text-[32px] leading-none font-bold text-[#0F172A]">
                    {formatCompactCurrency(overdueCollection)}
                  </p>
                  <p className="mt-0.5 text-[10px] tracking-wide font-semibold uppercase text-[#9AA6B2]">
                    Overdue Collection
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-3 gap-3 xl:items-stretch">
            <div className="flex flex-col gap-3 xl:col-span-2 min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <article className="rounded-xl border border-[#E7EBF1] bg-white overflow-hidden">
                  <header className="px-4 py-3 border-b border-[#EDF1F5]">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#4D6C72] inline-flex items-center gap-2">
                      <Building2 size={14} />
                      Basic Details
                    </h3>
                  </header>
                  <div className="p-4 space-y-3 text-sm">
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">Project Name</p>
                      <p className="text-[#1F2937] font-semibold">{project?.name || '--'}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">Project Type</p>
                      <p className="text-[#1F2937] font-semibold">
                        {project?.project_type || '--'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">Land Name</p>
                      <p className="text-[#1F2937] font-semibold">{project?.land?.name || '--'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div>
                        <p className="text-[#94A3B8] text-xs font-semibold uppercase">Start Date</p>
                        <p className="text-[#1F2937] font-semibold">
                          {convertDateToDDMMYYYY(project?.start_date || '') || '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#94A3B8] text-xs font-semibold uppercase">
                          Completion Date
                        </p>
                        <p className="text-[#1F2937] font-semibold">
                          {convertDateToDDMMYYYY(project?.completion_date || '') || '--'}
                        </p>
                      </div>
                    </div>
                  </div>
                </article>

                <article className="rounded-xl border border-[#E7EBF1] bg-white overflow-hidden">
                  <header className="px-4 py-3 border-b border-[#EDF1F5]">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#4D6C72] inline-flex items-center gap-2">
                      <CheckCircle2 size={14} />
                      Project Details
                    </h3>
                  </header>
                  <div className="p-4 space-y-3 text-sm">
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">
                        Building Permission Number
                      </p>
                      <p className="text-[#1F2937] font-semibold break-words">
                        {permissionNumbers}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">
                        RERA Registration Number
                      </p>
                      <p className="text-[#1F2937] font-semibold break-words">{reraNumbers}</p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8] text-xs font-semibold uppercase">
                        RERA Permission Expiry
                      </p>
                      <p
                        className={`font-semibold ${
                          reraExpiry === '--'
                            ? 'text-[#94A3B8]'
                            : hasActivePermission
                              ? 'text-[#0F766E]'
                              : 'text-[#DC2626]'
                        }`}>
                        {reraExpiry}
                      </p>
                    </div>
                  </div>
                </article>
              </div>

              <LateFee />
            </div>

            <article className="flex h-full min-h-0 flex-col rounded-xl border border-[#E7EBF1] bg-white overflow-hidden xl:col-span-1">
              <header className="shrink-0 px-4 py-3 border-b border-[#EDF1F5]">
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#4D6C72] inline-flex items-center gap-2">
                  <MapPin size={14} />
                  Land Details
                </h3>
              </header>
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-4 text-sm">
                <div>
                  <p className="text-[#94A3B8] text-xs font-semibold uppercase">{surveyLabel}</p>
                  <p className="text-[#1F2937] font-semibold">{surveyValue}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] text-xs font-semibold uppercase">Location</p>
                  <p className="text-[#1F2937] font-semibold">{location}</p>
                </div>
                <div>
                  <p className="text-[#94A3B8] text-xs font-semibold uppercase">Total Area</p>
                  <p className="text-[#1F2937] font-semibold">{displayArea || '--'}</p>
                </div>
                <a
                  href={googleMapUrl || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex shrink-0 items-center gap-1 font-semibold ${
                    googleMapUrl ? 'text-[#2563EB]' : 'text-[#94A3B8] pointer-events-none'
                  }`}>
                  <Landmark size={14} /> View on Google Maps
                </a>
                <div className="flex min-h-[120px] flex-1 flex-col">
                  {googlePreviewImageUrl ? (
                    <img
                      src={googlePreviewImageUrl}
                      alt="Google map preview"
                      className="min-h-[120px] flex-1 w-full rounded-lg object-cover border border-[#E7EBF1]"
                    />
                  ) : (
                    <div className="flex min-h-[120px] flex-1 items-center justify-center rounded-lg bg-[#F3F4F6] text-[#9CA3AF]">
                      <MapPin size={22} />
                    </div>
                  )}
                </div>
              </div>
            </article>
          </section>
        </div>
      </GridWrapper>
    </div>
    </>
  );
};

export default ProjectOverview;
